import React, { useState } from 'react';
import DropZone from '../components/DropZone';
import { bulkImportPreview, bulkImport } from '../api/api';
import { fmtId } from '../util/format';

export default function BulkImport() {
  const [bestand, setBestand] = useState(null);
  const [preview, setPreview] = useState(null);
  const [resultaat, setResultaat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Alleen CSV-bestanden.');
      return;
    }
    setBestand(file);
    setError(null);
    setLoading(true);
    setPreview(null);
    setResultaat(null);
    try {
      const formData = new FormData();
      formData.append('csv_file', file);
      const res = await bulkImportPreview(formData);
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const importeren = async () => {
    if (!bestand) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('csv_file', bestand);
      const res = await bulkImport(formData);
      setResultaat(res.data);
      setPreview(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setBestand(null);
    setPreview(null);
    setResultaat(null);
    setError(null);
  };

  return (
    <div className="main-inner">
      <div className="page-header">
        <div>
          <h1 className="page-title">Bulk-import CSV</h1>
          <p className="page-subtitle">Houders, inrichtingen en putten in één bestand</p>
        </div>
        {(preview || resultaat) && (
          <button className="btn btn-ghost" onClick={reset}>Andere CSV</button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!preview && !resultaat && (
        <>
          <DropZone
            accept=".csv,text/csv"
            onFile={onFile}
            label="Sleep een CSV-bestand hierheen"
            hint="of klik om te kiezen"
          />
          <div className="card" style={{ marginTop: 24 }}>
            <div className="card-header"><h3 className="card-title">Verwacht formaat</h3></div>
            <p style={{ marginTop: 0, color: 'var(--text-soft)', lineHeight: 1.6 }}>
              Eén rij per put. Houders en inrichtingen worden gededupliceerd op respectievelijk{' '}
              <code style={{ background: 'var(--surface-3)', padding: '2px 6px', borderRadius: 4 }}>kvk_nummer</code>{' '}
              (anders naam) en{' '}
              <code style={{ background: 'var(--surface-3)', padding: '2px 6px', borderRadius: 4 }}>verzoeknummer</code>.
              Putten worden gematched op coördinaat (binnen 5m).
            </p>
            <pre style={{
              background: 'var(--surface-3)',
              padding: 16,
              borderRadius: 'var(--radius-sm)',
              fontSize: 12.5,
              overflow: 'auto',
              margin: '16px 0 0',
              fontFamily: 'ui-monospace, monospace',
            }}>{`naam, kvk_nummer, type, voornaam, tussenvoegsel, achternaam,
adres, postcode, plaats, emailadres, telefoonnummer,
verzoeknummer, rechtstype, ingangsdatum, doel,
max_pompcap_m3_uur, bedrijfsomvang_ha,
x_rd, y_rd, opgegeven_diepte_m, max_diepte_mv,
diepte_bv_filter_m, perceel`}</pre>
          </div>
        </>
      )}

      {loading && <div className="empty-state"><span className="spinner" /></div>}

      {preview && (
        <>
          <div className="card">
            <div className="card-header"><h3 className="card-title">Samenvatting</h3></div>
            <div className="field-grid cols-3">
              <div className="field">
                <span className="field-label">Totaal rijen</span>
                <span className="field-value">{preview.totaal_rijen}</span>
              </div>
              <div className="field">
                <span className="field-label">Unieke houders</span>
                <span className="field-value">{preview.unieke_houders}</span>
              </div>
              <div className="field">
                <span className="field-label">Unieke inrichtingen</span>
                <span className="field-value">{preview.unieke_inrichtingen}</span>
              </div>
              <div className="field">
                <span className="field-label">Putten</span>
                <span className="field-value">{preview.putten}</span>
              </div>
              <div className="field">
                <span className="field-label">Fouten</span>
                <span className="field-value" style={{ color: preview.fouten.length ? 'var(--danger)' : 'var(--success)' }}>
                  {preview.fouten.length}
                </span>
              </div>
            </div>
          </div>

          {preview.fouten.length > 0 && (
            <div className="card">
              <div className="card-header"><h3 className="card-title">Fouten</h3></div>
              <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--danger)' }}>
                {preview.fouten.map((f, i) => <li key={i} style={{ marginBottom: 4 }}>{f}</li>)}
              </ul>
            </div>
          )}

          <div className="card" style={{ padding: 0 }}>
            <div className="card-header" style={{ padding: 24, marginBottom: 0 }}>
              <h3 className="card-title">Voorvertoning</h3>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Rij</th>
                  <th>Houder</th>
                  <th>KvK</th>
                  <th>Inrichting</th>
                  <th>Verzoek</th>
                  <th>Coords</th>
                  <th>Put</th>
                </tr>
              </thead>
              <tbody>
                {preview.preview.map(p => (
                  <tr key={p.rij}>
                    <td>{p.rij}</td>
                    <td>
                      {p.houder_naam}
                      {p.houder_match_id && <span className="chip chip-success" style={{ marginLeft: 8 }}>bestaand</span>}
                    </td>
                    <td>{p.houder_kvk || '—'}</td>
                    <td>
                      {p.inrichting_match_id ? <span className="mono">{fmtId(p.inrichting_match_id)}</span> : <span style={{ color: 'var(--text-muted)' }}>nieuw</span>}
                    </td>
                    <td>{p.verzoeknummer}</td>
                    <td>{p.put_coords[0]}, {p.put_coords[1]}</td>
                    <td>
                      {p.put_match_id ? (
                        <span className="chip chip-accent">{fmtId(p.put_match_id)}</span>
                      ) : (
                        <span className="chip">nieuw</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row end" style={{ marginTop: 32 }}>
            <button className="btn btn-ghost" onClick={reset}>Annuleren</button>
            <button className="btn btn-primary btn-lg" onClick={importeren} disabled={loading}>
              {loading ? <><span className="spinner" /> Bezig...</> : 'Definitief importeren'}
            </button>
          </div>
        </>
      )}

      {resultaat && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Import voltooid</h3>
            <span className="chip chip-success">{resultaat.status}</span>
          </div>
          <div className="field-grid cols-3">
            <div className="field">
              <span className="field-label">Aangemaakte houders</span>
              <span className="field-value">{resultaat.aangemaakte_houders}</span>
            </div>
            <div className="field">
              <span className="field-label">Aangemaakte inrichtingen</span>
              <span className="field-value">{resultaat.aangemaakte_inrichtingen}</span>
            </div>
            <div className="field">
              <span className="field-label">Aangemaakte putten</span>
              <span className="field-value">{resultaat.aangemaakte_putten}</span>
            </div>
            <div className="field">
              <span className="field-label">Hergebruikte putten</span>
              <span className="field-value">{resultaat.hergebruikte_putten}</span>
            </div>
          </div>
          {resultaat.fouten?.length > 0 && (
            <>
              <hr className="divider" />
              <strong style={{ color: 'var(--danger)' }}>Fouten:</strong>
              <ul style={{ margin: '8px 0 0 20px', color: 'var(--danger)' }}>
                {resultaat.fouten.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
